import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { PortalType } from '../../../../../config/enum';
import { usePortal } from '../../../../../contexts/PortalContext/PortalContext';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import { setNotificationMessage } from '../../../../../redux/slices/NotificationManagementSlice';
import { fetchAllStationsList } from '../../../../../redux/slices/Partners/ApprovedLocationManagementSlice';
import MyButton from '../../../../../shared-components/button/Button';
import CustomInput from '../../../../../shared-components/custom-input/CustomInput';
import ModalBox from '../../../../../shared-components/modal-box/ModalBox';
import RadioButton from '../../../../../shared-components/radio-button/RadioButton';
import SelectFiled from '../../../../../shared-components/select-field/SelectField';
import SuccessNotification from '../../../../../shared-components/success-notification/SuccessNotification';
import ConnectorSidePanel from '../../../../../shared-components/table-side-panel/ConnectorSidePanel';
import devicePartnerApi from '../../../../../utils/api/partner/device-partner';
import AddFacilityAccount from '../../../../account-management/components/add-facility-account';

interface EditDeviceFormProperties {
  deviceDetails: any;
  closeModal: () => void;
  onSuccess: () => void;
}

/**
 * EditDeviceForm component to handle editing a charger's details.
 * @param {EditDeviceFormProperties} root0 The component properties.
 * @param {any} root0.deviceDetails The device details to edit.
 * @param {() => void} root0.closeModal Callback to close the modal.
 * @param {() => void} root0.onSuccess Callback on successful submit.
 * @returns {any} The rendered EditDeviceForm component.
 */
function EditDeviceForm({ deviceDetails, closeModal, onSuccess }: EditDeviceFormProperties) {
  const { t } = useTranslation(['partnerDeviceManagement', 'station']);
  const dispatch = useAppDispatch();
  const { partnerStations } = useAppSelector((state) => state.partnersApprovedLocation);
  const { portalType } = usePortal();

  const [editData, setEditData] = useState<any>({
    chargingStation: '',
    chargingPointName: '',
    chargingPointSn: '',
    chargePointModel: '',
    chargePointVendor: '',
    connectors: [],
    maximumOutputPower: 0,
    maximumOutputPowerUnit: 'kW',
    desiredPowerOutput: 0,
    powerUnit: 'W',
    modelSeries: '',
    currentType: '',
    accountCustomId: '',
    skuNumber: '',
    installationType: '',
    networkConnectivity: '',
    plugAndCharge: '',
    firmwareVersion: '',
    stationLocation: '',
  });

  const [allStationsList, setAllStationsList] = useState<any[]>([]);
  const [accountNameList, setAccountNameList] = useState<any[]>([]);
  const [buttonLoader, setButtonLoader] = useState(false);
  const [modalBoxStatus, setModalBoxStatus] = useState<'open' | 'close'>('close');
  const [modalBoxDetail, setModalBoxDetail] = useState<any>({
    title: '',
    content: null,
    notificationModal: false,
  });

  // Initialize edit fields
  useEffect(() => {
    if (deviceDetails) {
      const stationIdValue =
        typeof deviceDetails.chargingStation === 'object'
          ? deviceDetails.chargingStation?._id || deviceDetails.chargingStation?.id
          : deviceDetails.chargingStation;

      setEditData({
        chargingStation: stationIdValue || '',
        chargingPointName: deviceDetails.chargingPointName || '',
        chargingPointSn: deviceDetails.chargingPointSn || '',
        chargePointModel: deviceDetails.chargePointModel || '',
        chargePointVendor: deviceDetails.chargePointVendor || '',
        connectors: deviceDetails.connectors || [],
        maximumOutputPower: deviceDetails.maximumOutputPower || 0,
        maximumOutputPowerUnit: deviceDetails.maximumOutputPowerUnit || 'kW',
        desiredPowerOutput: deviceDetails.chargeRate?.value
          ? Number(deviceDetails.chargeRate.value) / 1000
          : 0,
        powerUnit: deviceDetails.chargeRate?.unit || 'W',
        modelSeries: deviceDetails.modelSeries || '',
        currentType: deviceDetails.currentType || '',
        accountCustomId: deviceDetails.accountCustomId || '',
        skuNumber: deviceDetails.skuNumber || '',
        installationType: deviceDetails.installationType || '',
        networkConnectivity: deviceDetails.networkConnectivity || '',
        plugAndCharge:
          deviceDetails.plugAndCharge !== undefined ? String(deviceDetails.plugAndCharge) : '',
        firmwareVersion: deviceDetails.firmwareVersion || '',
      });
    }
  }, [deviceDetails]);

  /**
   * Fetch account names list for autocomplete options.
   * @returns {Promise<void>} Resolves when account list is loaded.
   */
  const handleAccountNameList = async () => {
    try {
      const response = await devicePartnerApi.fetchAccountNameList();
      setAccountNameList(
        response.data.map((account: any) => ({
          label: account?.accountName,
          value: account?.accountCustomId,
        })),
      );
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  /**
   * Open Add Partner modal.
   * @returns {void}
   */
  const handleOpenAddPartner = () => {
    setModalBoxDetail({
      title: '',
      content: (
        <AddFacilityAccount
          closeModal={(message) => {
            setModalBoxStatus('close');
            if (message) {
              handleAccountNameList();
              dispatch(
                setNotificationMessage({
                  message,
                  status: true,
                  type: 'success',
                }),
              );
            }
          }}
          sidebarClosed={() => setModalBoxStatus('close')}
        />
      ),
      notificationModal: false,
    });
    setModalBoxStatus('open');
  };

  /**
   * Fetch stations list belonging to the specified account.
   * @param {string} accountId Account custom ID.
   * @returns {Promise<void>} Resolves when station list is loaded.
   */
  const handleStationList = async (accountId: string) => {
    if (!accountId) return;
    try {
      const response = await devicePartnerApi.fetchChargingStationList(accountId);
      const mappedStations = (response.data || []).map((station: any) => ({
        label: station.name || '',
        value: station._id || station.id || '',
        description: station?.address || '',
      }));
      setAllStationsList(mappedStations);
    } catch (error: any) {
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  useEffect(() => {
    if (PortalType.NETWORKS === portalType || PortalType.APP === portalType) {
      handleAccountNameList();
    } else if (!partnerStations || partnerStations.length === 0) {
      dispatch(fetchAllStationsList());
    }
  }, [portalType]);

  useEffect(() => {
    if (editData.accountCustomId && editData.accountCustomId.length === 20) {
      handleStationList(editData.accountCustomId);
    }
  }, [editData.accountCustomId]);

  /**
   * Handler for form inputs' values change.
   * @param {string} name The name of the field.
   * @param {any} value The updated value.
   * @returns {void}
   */
  const handleInputChange = (name: string, value: any) => {
    setEditData((previous: any) => ({
      ...previous,
      [name]: value,
    }));
  };

  /**
   * Submit edited form data to editDevice API.
   * @returns {Promise<void>} Resolves when update is completed.
   */
  const handleSubmit = async () => {
    if (!editData.chargingStation) {
      dispatch(
        setNotificationMessage({
          message: t('stationNameRequired'),
          status: true,
          type: 'error',
        }),
      );
      return;
    }
    if (!editData.chargingPointName) {
      dispatch(
        setNotificationMessage({
          message: t('deviceNameRequired'),
          status: true,
          type: 'error',
        }),
      );
      return;
    }

    if (
      Number(editData.maximumOutputPower) > 0 &&
      Number(editData.desiredPowerOutput) > Number(editData.maximumOutputPower)
    ) {
      dispatch(
        setNotificationMessage({
          message: `${t(
            'desiredPowerCannotExceedMaxPower',
            'Desired power cannot exceed maximum power limit',
          )} (${editData.maximumOutputPower})`,
          status: true,
          type: 'error',
        }),
      );
      return;
    }

    setButtonLoader(true);

    const { desiredPowerOutput, powerUnit, modelSeries, connectors, accountCustomId, ...rest } =
      editData;

    let plugAndChargeValue: boolean | undefined;
    if (editData.plugAndCharge === 'true') {
      plugAndChargeValue = true;
    } else if (editData.plugAndCharge === 'false') {
      plugAndChargeValue = false;
    }

    const payload: any = {
      ...rest,
      plugAndCharge: plugAndChargeValue,
      chargeRate: {
        unit: powerUnit,
        value: String(powerUnit === 'W' ? Number(desiredPowerOutput) * 1000 : desiredPowerOutput),
      },
    };

    if (!payload.currentType) {
      delete payload.currentType;
    }

    try {
      await devicePartnerApi.editDevice(payload, deviceDetails._id || deviceDetails.id);
      setButtonLoader(false);
      setModalBoxStatus('open');
      setModalBoxDetail({
        title: null,
        content: (
          <SuccessNotification
            title={t('deviceUpdatedSuccess', 'Charger Updated Successfully')}
            description={t('deviceUpdatedDescription', 'Charger details updated successfully.')}
            notificationCloseTimer={3000}
            onNotificationClose={() => {
              setModalBoxStatus('close');
              onSuccess();
            }}
          />
        ),
        notificationModal: true,
      });
    } catch (error: any) {
      setButtonLoader(false);
      dispatch(
        setNotificationMessage({
          message: error?.response?.data?.message || t('somethingWentWrong'),
          status: true,
          type: 'error',
          code: error?.response?.data?.code,
        }),
      );
    }
  };

  return (
    <div className="add-device-panel-partner edit-device-form">
      <ModalBox
        open={modalBoxStatus === 'open'}
        title={modalBoxDetail.title}
        content={modalBoxDetail.content}
        closeModal={() => setModalBoxStatus('close')}
        modalBoxAnimation="modalAnimateToLeft"
        notification={modalBoxDetail.notificationModal}
        header={!modalBoxDetail.notificationModal}
      />
      <div className="panel-form">
        <div className="form-grid">
          <div className="full-width-field">
            <h4>{t('basicInfo')}</h4>
          </div>

          {(PortalType.NETWORKS === portalType || PortalType.APP === portalType) && (
            <SelectFiled
              name="accountCustomId"
              inputLabel={t('accountName')}
              placeholder={t('enterAccountName')}
              options={accountNameList}
              selectedValue={editData.accountCustomId}
              onChangeValue={handleInputChange}
              error={!editData.accountCustomId}
              errorMessage={t('accountNameRequired')}
              valueRequired
              enableSearch
              emptyActionLabel={t('addPartner', 'Add Partner')}
              onEmptyActionClick={handleOpenAddPartner}
            />
          )}

          <SelectFiled
            inputLabel={t('stationName')}
            name="chargingStation"
            placeholder={t('selectStation')}
            options={
              PortalType.NETWORKS === portalType || PortalType.APP === portalType
                ? allStationsList
                : partnerStations
            }
            selectedValue={editData.chargingStation}
            onChangeValue={handleInputChange}
            error={!editData.chargingStation}
            errorMessage={t('stationNameRequired')}
            valueRequired
            enableSearch
          />

          <CustomInput
            inputLabel={t('deviceName')}
            type="text"
            name="chargingPointName"
            placeholder={t('enterDeviceName', 'Enter the charger name')}
            value={editData.chargingPointName}
            valueRequired
            onChangeValue={handleInputChange}
            error={!editData.chargingPointName}
            errorMessage={t('deviceNameRequired')}
          />

          <CustomInput
            inputLabel={t('desiredPowerOutputKw', 'Desired Power Output (kW)')}
            type="number"
            name="desiredPowerOutput"
            placeholder={t('enterValue')}
            value={editData.desiredPowerOutput}
            onChangeValue={handleInputChange}
            error={
              Number(editData.maximumOutputPower) > 0 &&
              Number(editData.desiredPowerOutput) > Number(editData.maximumOutputPower)
            }
            errorMessage={`${t(
              'desiredPowerCannotExceedMaxPower',
              'Desired power cannot exceed maximum power limit',
            )} (${editData.maximumOutputPower})`}
            decimalLimit={3}
            maxValue={1000}
          />

          <CustomInput
            inputLabel={t('deviceId')}
            type="text"
            name="chargingPointSn"
            placeholder={t('enterDeviceId', 'Enter the ID number')}
            value={editData.chargingPointSn}
            disabled
          />
        </div>

        <div className="form-grid">
          <div className="full-width-field">
            <h4>{t('chargerInfo')}</h4>
          </div>

          <CustomInput
            inputLabel={t('maximumPowerOutputKw', 'Maximum Power Output (kW)')}
            type="number"
            name="maximumOutputPower"
            placeholder={t('enterValue')}
            value={editData.maximumOutputPower}
            disabled
          />

          <CustomInput
            inputLabel={t('chargePointVendor')}
            type="text"
            name="chargePointVendor"
            placeholder={t('chargePointVendor')}
            value={editData.chargePointVendor}
            disabled
          />

          <CustomInput
            inputLabel={t('chargePointModel')}
            type="text"
            name="chargePointModel"
            placeholder={t('chargePointModel')}
            value={editData.chargePointModel}
            disabled
          />

          <CustomInput
            inputLabel={t('skuNumber', 'SKU Number')}
            type="text"
            placeholder={t('skuNumber', 'SKU Number')}
            name="skuNumber"
            value={editData.skuNumber || ''}
            onChangeValue={handleInputChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />

          <CustomInput
            inputLabel={t('installationType', 'Installation Type')}
            type="text"
            placeholder={t('installationType', 'Installation Type')}
            name="installationType"
            value={editData.installationType || ''}
            onChangeValue={handleInputChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />

          <CustomInput
            inputLabel={t('networkConnectivity', 'Network Connectivity')}
            type="text"
            placeholder={t('networkConnectivity', 'Network Connectivity')}
            name="networkConnectivity"
            value={editData.networkConnectivity || ''}
            onChangeValue={handleInputChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />

          <RadioButton
            inputLabel={t('plugAndCharge', 'Plug and Charge')}
            name="plugAndCharge"
            value={editData.plugAndCharge}
            onChangeValue={handleInputChange}
            list={[
              { label: t('yes', 'Yes'), value: 'true' },
              { label: t('no', 'No'), value: 'false' },
            ]}
            isDisabled
          />

          <CustomInput
            inputLabel={t('currentType')}
            type="text"
            name="currentType"
            placeholder={t('currentType')}
            value={editData.currentType || ''}
            disabled
          />

          <CustomInput
            inputLabel={t('firmwareVersion')}
            type="text"
            placeholder={t('firmwareVersion')}
            valueRequired
            name="firmwareVersion"
            value={editData.firmwareVersion || ''}
            onChangeValue={handleInputChange}
            tooltipText={t(
              'autoPopulatedDeviceIdTooltip',
              'This field is automatically populated based on the Device ID and cannot be edited.',
            )}
            disabled
          />
        </div>

        <div className="connector-panel-wrapper">
          <ConnectorSidePanel
            connectors={editData.connectors}
            currentType={editData.currentType}
            showHeader={false}
            id={editData._id || editData.id}
            chargingPointSn={editData.chargingPointSn}
          />
        </div>
      </div>

      <div className="panel-footer">
        <MyButton label={t('cancel')} buttonType="back" onClickFunc={closeModal} />
        <MyButton
          label={t('submit')}
          buttonType="submit"
          onClickFunc={handleSubmit}
          disableButton={buttonLoader}
        />
      </div>
    </div>
  );
}

export default EditDeviceForm;
